import { useAuth } from "@clerk/clerk-react";
import { CaretDownIcon, FolderIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { galleryTreeQuery } from "../../lib/queries";
import {
  type ProjectMenuItem,
  projectMenuItems,
} from "../../lib/source-catalog";

interface AddToAssetsButtonProps {
  inLibrary: boolean;
  onSave: () => void;
  onAddToProject: (project: ProjectMenuItem) => void;
}

const SEGMENT =
  "flex h-9 items-center bg-[var(--btn-modal-primary)] text-sm leading-5 font-semibold text-[#f0f0f0] transition-colors hover:brightness-110 disabled:cursor-default disabled:hover:brightness-100";

/**
 * "Add to: My Assets", split: the label saves the product to the library, and
 * the caret opens the user's projects to file it in one of them as well.
 *
 * The open menu hangs off the button's bottom edge, so the button squares its
 * lower corners to join it. Escape closes the menu before the modal: the menu
 * claims the key in the capture phase, as ``AssetPreviewLightbox`` does.
 */
export function AddToAssetsButton({
  inLibrary,
  onSave,
  onAddToProject,
}: AddToAssetsButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const close = useCallback(() => setIsOpen(false), []);
  useCloseOnOutsideOrEscape(isOpen, containerRef, close);
  const corners = isOpen ? "rounded-t-lg" : "rounded-lg";

  return (
    <div ref={containerRef} className="relative">
      <div className={`flex overflow-hidden ${corners}`}>
        <button
          type="button"
          onClick={onSave}
          disabled={inLibrary}
          className={`${SEGMENT} pr-2 pl-4`}
        >
          {inLibrary ? "In My Assets" : "Add to: My Assets"}
        </button>
        <button
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          aria-label="Add to a project"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-controls={isOpen ? menuId : undefined}
          className={`${SEGMENT} pr-3 pl-1`}
        >
          <CaretDownIcon size={16} />
        </button>
      </div>
      {isOpen && (
        <ProjectMenu
          id={menuId}
          onPick={(project) => {
            setIsOpen(false);
            onAddToProject(project);
          }}
        />
      )}
    </div>
  );
}

function useCloseOnOutsideOrEscape(
  isOpen: boolean,
  containerRef: React.RefObject<HTMLDivElement | null>,
  close: () => void,
) {
  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      close();
    };
    document.addEventListener("mousedown", onMouseDown);
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [isOpen, containerRef, close]);
}

function ProjectMenu({
  id,
  onPick,
}: {
  id: string;
  onPick: (project: ProjectMenuItem) => void;
}) {
  const { orgId } = useAuth();
  const { data, isPending, isError } = useQuery(galleryTreeQuery(orgId));
  const items = projectMenuItems(data?.projects ?? []);

  return (
    <div
      id={id}
      role="menu"
      aria-label="Projects"
      className="absolute top-full right-0 z-10 flex max-h-72 w-max min-w-[174px] max-w-72 flex-col gap-2 overflow-y-auto rounded-b-lg bg-[var(--bg-default)] py-4 pr-4 pl-3 shadow-lg"
    >
      {isPending || isError || items.length === 0 ? (
        <p className="px-1 text-sm text-[var(--text-muted)]">
          {isPending
            ? "Loading projects…"
            : isError
              ? "Couldn't load projects."
              : "No projects yet."}
        </p>
      ) : (
        items.map((project) => (
          <button
            key={project.id}
            type="button"
            role="menuitem"
            onClick={() => onPick(project)}
            style={{ paddingLeft: `${4 + project.depth * 16}px` }}
            className="flex w-full items-center gap-2 rounded py-0.5 pr-1 text-left text-sm leading-[1.6] text-[#f0f0f0] hover:bg-[rgba(60,124,165,0.5)] focus-visible:bg-[rgba(60,124,165,0.5)] focus-visible:outline-none"
          >
            <FolderIcon size={16} className="shrink-0" />
            <span className="truncate">{project.name}</span>
          </button>
        ))
      )}
    </div>
  );
}
